import {
  getResolvedOrResolveTheme,
  registerCustomTheme,
  type ThemeRegistrationResolved,
} from "@pierre/diffs";

export const READABLE_LIGHT_THEME_NAME = "amoreg-light";

type Rgb = readonly [number, number, number];

const MIN_CONTRAST = 4.5;
const CHANGED_WORD_ON_DELETED_LINE: Rgb = [0xf8, 0xd3, 0xd4];
const SURFACE_LUMINANCE_MARGIN = 0.02;

function channelLuminance(channel: number): number {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function luminance([r, g, b]: Rgb): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

const DARKEST_SURFACE_LUMINANCE =
  luminance(CHANGED_WORD_ON_DELETED_LINE) - SURFACE_LUMINANCE_MARGIN;

function contrastOnLightCode(rgb: Rgb): number {
  return (DARKEST_SURFACE_LUMINANCE + 0.05) / (luminance(rgb) + 0.05);
}

export function readableOnLightCode(color: string, minContrast = MIN_CONTRAST): string {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(color);
  if (!match) return color;
  const hex = match[1]!;
  const channel = (start: number) => Number.parseInt(hex.slice(start, start + 2), 16);
  let rgb: Rgb = [channel(0), channel(2), channel(4)];
  if (contrastOnLightCode(rgb) >= minContrast) return color;
  while (contrastOnLightCode(rgb) < minContrast) {
    const [r, g, b] = rgb;
    rgb = [Math.floor(r * 0.96), Math.floor(g * 0.96), Math.floor(b * 0.96)];
  }
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}${match[2] ?? ""}`;
}

const LIGHTNESS_ONLY_PAIRS: ReadonlyArray<readonly [lighter: string, darker: string]> = [
  ["#737373", "#636363"],
  ["#08c0ef", "#1ca1c7"],
  ["#009fff", "#1a85d4"],
];
const DARKER_OF_PAIR = new Set(LIGHTNESS_ONLY_PAIRS.map(([, darker]) => darker));
const DARKER_OF_PAIR_MIN_CONTRAST = 6;

export function readableSyntaxColor(color: string): string {
  return readableOnLightCode(
    color,
    DARKER_OF_PAIR.has(color.toLowerCase()) ? DARKER_OF_PAIR_MIN_CONTRAST : MIN_CONTRAST,
  );
}

export function withReadableSyntax(theme: ThemeRegistrationResolved): ThemeRegistrationResolved {
  const settings = theme.settings.map((rule) => {
    const foreground = rule.settings.foreground;
    return foreground
      ? { ...rule, settings: { ...rule.settings, foreground: readableSyntaxColor(foreground) } }
      : rule;
  });
  return { ...theme, name: READABLE_LIGHT_THEME_NAME, settings };
}

// Themes resolve on the main thread and reach the diff workers already resolved.
registerCustomTheme(READABLE_LIGHT_THEME_NAME, async () =>
  withReadableSyntax(await getResolvedOrResolveTheme("pierre-light")),
);
